import React from "react"
import { AnchorProps } from "react-bootstrap"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { connect } from "scrivito"
import { BuildingReferenceList } from "scrivito/Objs/BuildingReference/Components/BuildingReferenceList"

import { ScrivitoBuildingReferenceCard } from "entities/building-references/ui/scrivito-building-reference-card/ScrivitoBuildingReferenceCard"

import { useProductPageContext } from "../context/ProductPageContext"

export interface BuildingReferenceListItemType extends AnchorProps {
  id: string
  title: string | undefined
  image?: React.ReactNode
}

type ProductPageBuildingReferencesProps = Omit<
  React.ComponentPropsWithoutRef<"section">,
  "ref"
> & {
  items?: BuildingReferenceListItemType[]
  ItemRenderer?: React.ComponentType<BuildingReferenceListItemType>
}

function ProductPageBuildingReferencesComponent({
  className,
  items,
  ItemRenderer,
  ...props
}: ProductPageBuildingReferencesProps) {
  const { t } = useTranslation()
  const {
    buildingReferenceObjs,
    buildingReferenceItemRenderer: ItemRendererFromContext
  } = useProductPageContext()

  if (items !== undefined && ItemRenderer !== undefined) {
    if (items.length === 0) {
      return null
    }

    return (
      <section
        className={classNames(
          "product-page__building-references product-page__section card card--elevated",
          className
        )}
        {...props}
      >
        <h2 className="product-page__section-title">
          {t("Product.References")}
        </h2>
        <BuildingReferenceList items={items} ItemRenderer={ItemRenderer} />
      </section>
    )
  }

  const references = buildingReferenceObjs ?? []

  if (references.length === 0) {
    return null
  }

  return (
    <section
      className={classNames(
        "product-page__building-references product-page__section card card--elevated",
        className
      )}
      {...props}
    >
      <h2 className="product-page__section-title">{t("Product.References")}</h2>
      <div className="product-page__building-reference-grid">
        {references.map(buildingReferenceObj =>
          ItemRendererFromContext ? (
            <ItemRendererFromContext
              key={buildingReferenceObj.id()}
              buildingReferenceObj={buildingReferenceObj}
            />
          ) : (
            <ScrivitoBuildingReferenceCard.Root
              key={buildingReferenceObj.id()}
              buildingReferenceObj={buildingReferenceObj}
              className="card--elevated"
            >
              <ScrivitoBuildingReferenceCard.Title />
              <ScrivitoBuildingReferenceCard.Image />
              <ScrivitoBuildingReferenceCard.InfoText />
            </ScrivitoBuildingReferenceCard.Root>
          )
        )}
      </div>
    </section>
  )
}

export const ProductPageBuildingReferences = React.memo(
  connect(ProductPageBuildingReferencesComponent)
) as typeof ProductPageBuildingReferencesComponent
